import React, { useState } from 'react';
import { Shield, DollarSign, Image, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const ContentPreferences = () => {
  const [settings, setSettings] = useState({
    defaultLicenseType: 'personal',
    defaultPricing: 15.00,
    watermarkEnabled: true,
    watermarkText: 'I Got Soul Creator',
    contentVisibility: 'public'
  });

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-6">
      {/* Default Licensing */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Default Licensing Type
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div 
              className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                settings.defaultLicenseType === 'personal' 
                  ? 'border-yellow-400 bg-yellow-400/10' 
                  : 'border-gray-600 hover:border-gray-500'
              }`}
              onClick={() => handleSettingChange('defaultLicenseType', 'personal')}
            >
              <h4 className="text-white font-medium">Personal Use</h4>
              <p className="text-gray-400 text-sm">For personal projects only</p>
            </div>
            
            <div 
              className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                settings.defaultLicenseType === 'commercial' 
                  ? 'border-yellow-400 bg-yellow-400/10' 
                  : 'border-gray-600 hover:border-gray-500'
              }`}
              onClick={() => handleSettingChange('defaultLicenseType', 'commercial')}
            >
              <h4 className="text-white font-medium">Commercial Use</h4>
              <p className="text-gray-400 text-sm">For business & marketing</p>
            </div>
            
            <div 
              className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                settings.defaultLicenseType === 'exclusive' 
                  ? 'border-yellow-400 bg-yellow-400/10' 
                  : 'border-gray-600 hover:border-gray-500'
              }`}
              onClick={() => handleSettingChange('defaultLicenseType', 'exclusive')}
            >
              <h4 className="text-white font-medium">Exclusive Rights</h4>
              <p className="text-gray-400 text-sm">Full ownership transfer</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pricing Settings */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Default Pricing Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-white">Base Price per Download</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">$</span>
                <Input
                  type="number"
                  value={settings.defaultPricing}
                  onChange={(e) => handleSettingChange('defaultPricing', parseFloat(e.target.value))}
                  className="bg-gray-700 border-gray-600 text-white pl-8"
                  min="1"
                  max="1000"
                  step="0.01"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-white">Currency</Label>
              <Select defaultValue="USD">
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600">
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="GBP">GBP (£)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="bg-blue-900/20 border border-blue-500/20 rounded-lg p-4">
            <h4 className="text-blue-400 font-medium mb-2">Pricing Recommendations</h4>
            <div className="text-sm text-gray-300 space-y-1">
              <p>• Music Videos: $10 - $25</p>
              <p>• Short Films: $15 - $50</p>
              <p>• Stock Footage: $5 - $20</p>
              <p>• Exclusive Content: $25 - $100+</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Watermark Options */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Image className="w-5 h-5" />
            Watermark Options
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-white font-medium">Enable Watermark</h4>
              <p className="text-gray-400 text-sm">Protect your content with watermarks</p>
            </div>
            <Switch
              checked={settings.watermarkEnabled}
              onCheckedChange={(checked) => handleSettingChange('watermarkEnabled', checked)}
            />
          </div>
          
          {settings.watermarkEnabled && (
            <div className="space-y-4 pt-4 border-t border-gray-600">
              <div className="space-y-2">
                <Label className="text-white">Watermark Text</Label>
                <Input
                  value={settings.watermarkText}
                  onChange={(e) => handleSettingChange('watermarkText', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="Your watermark text"
                  maxLength={50}
                />
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700">
                  Bottom Right
                </Button>
                <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700">
                  Bottom Left
                </Button>
                <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700">
                  Center
                </Button>
                <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700">
                  Custom
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Content Visibility */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Content Visibility
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div 
              className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                settings.contentVisibility === 'public' 
                  ? 'border-green-400 bg-green-400/10' 
                  : 'border-gray-600 hover:border-gray-500'
              }`}
              onClick={() => handleSettingChange('contentVisibility', 'public')}
            >
              <h4 className="text-white font-medium">Public</h4>
              <p className="text-gray-400 text-sm">Visible to everyone</p>
            </div>
            
            <div 
              className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                settings.contentVisibility === 'followers' 
                  ? 'border-yellow-400 bg-yellow-400/10' 
                  : 'border-gray-600 hover:border-gray-500'
              }`}
              onClick={() => handleSettingChange('contentVisibility', 'followers')}
            >
              <h4 className="text-white font-medium">Followers Only</h4>
              <p className="text-gray-400 text-sm">Only your followers</p>
            </div>
            
            <div 
              className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                settings.contentVisibility === 'private' 
                  ? 'border-red-400 bg-red-400/10' 
                  : 'border-gray-600 hover:border-gray-500'
              }`}
              onClick={() => handleSettingChange('contentVisibility', 'private')}
            >
              <h4 className="text-white font-medium">Private</h4>
              <p className="text-gray-400 text-sm">Only you can see</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContentPreferences;
